import { withDependencies, optional } from '@wix/thunderbolt-ioc'
import type { IAppWillLoadPageHandler, BrowserWindow } from '@wix/thunderbolt-symbols'
import { BrowserWindowSymbol } from '@wix/thunderbolt-symbols'
import { createMpaSessionCache } from '@wix/thunderbolt-commons'
import type { ILightbox } from './types'
import { LightboxSymbol } from './symbols'

const POPUP_PARAM = 'popup'

export const autoDisplayLightboxByParamFactory = (
	window: BrowserWindow,
	lightbox?: ILightbox
): IAppWillLoadPageHandler => ({
	name: 'autoDisplayLightboxByParam',
	appWillLoadPage() {
		if (!window || !lightbox) {
			return
		}

		const popupId = new URLSearchParams(window.location.search).get(POPUP_PARAM)
		if (!popupId || !lightbox.isLightbox(popupId)) {
			return
		}

		// Same session key as the scheduled auto-display flow (feature-auto-display-lightbox)
		// so the same popup isn't auto-opened twice in a session. The cache resets on hard
		// reload, so a reload behaves like a fresh visit.
		const popupShownCache = createMpaSessionCache<boolean>({
			browserWindow: window,
			storageKey: `wix-auto-popup-shown-${popupId}`,
		})
		if (popupShownCache.read() === true) {
			return
		}

		// Mark synchronously so a concurrently-scheduled auto-display is suppressed before
		// its delayed open fires; clear on failure so a transient open() error still allows
		// a retry on a later navigation.
		popupShownCache.write(true)
		void lightbox.open(popupId).catch(() => popupShownCache.clear())
	},
})

export const AutoDisplayLightboxByParam = withDependencies(
	[BrowserWindowSymbol, optional(LightboxSymbol)],
	autoDisplayLightboxByParamFactory
)
